import React, { useId } from "react";
import { maxBy, sortBy } from "lodash";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Loading from "@components/shared/Loading";
import Tag from "@components/shared/Tag";
import LoadFailed, { showsOwnFailure } from "@components/zenith/ui/LoadFailed";
import { useSubscriberLevel0Resource } from "@hooks/query/zenith/useSubscriberLevel0Resource";
import { formatVolume } from "@utils/formatVolume";
import { formatSchemeDay, formatSchemePeriod } from "@utils/schemeDay";
import { allocationOf, volumesOf } from "@utils/volumes";
import {
  extractionRightTypes,
  getExtractionRightTypeIdentifier,
} from "@services/extractionRight";
import { useZoneOfftakes } from "./OfftakesAndMetersTab";

const ZoneAccountSummary: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { id: level0ResourceId = "" } = useParams();
  // The page header loads the same query, so this reads its cache.
  const {
    data: zone,
    isLoading,
    isError,
  } = useSubscriberLevel0Resource({
    params: { level0ResourceId },
    ...showsOwnFailure,
  });

  // The Offtakes tab's query: its newest read dates each class's remaining balance.
  const { data: points } = useZoneOfftakes();

  if (isLoading) return <Loading />;
  if (isError) return <LoadFailed />;

  // The zone's newest read, or the period's start when nothing was read since;
  // unknown until the reads have loaded.
  const asAt =
    points &&
    maxBy(
      [
        zone.accountingPeriod?.periodStart,
        ...points.map((point: any) => point.lastDeclaration?.readAt),
      ].filter(Boolean),
      (date: string) => Date.parse(date),
    );

  const noPeriod = zone.balance === null;
  const figure = (litres: number) =>
    noPeriod ? t("zenith.dashboard.no_active_period") : formatVolume(litres);

  const hasOtherClass = (type: keyof typeof extractionRightTypes) =>
    zone.otherClasses?.some(
      (otherClass: { type: string }) =>
        otherClass.type ===
        getExtractionRightTypeIdentifier(extractionRightTypes[type]),
    );
  const otherUsages = [
    {
      label: t("approval.stock_and_domestic.title"),
      usage: zone.stockAndDomesticUsage,
      show: hasOtherClass("SD"),
    },
    {
      label: t("declaration.water_harvesting"),
      usage: zone.waterHarvestingUsage,
      show: hasOtherClass("WH"),
    },
    {
      label: t("subscriber.special_announcement"),
      usage: zone.specialAnnouncementUsage,
      show: hasOtherClass("SA"),
    },
  ].filter(otherUsage => otherUsage.show);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-6">
        <Section title={t("zenith.zone_accounts.zone_details")}>
          <Rows
            rows={[
              [t("common.identifier"), zone.identifier],
              [t("common.name"), zone.name],
              [
                t("common.accounting_period"),
                formatSchemePeriod(zone.accountingPeriod),
              ],
            ]}
          />
        </Section>
        {otherUsages.length ? (
          <Section title={t("subscriber.volume_other")}>
            <Rows
              rows={otherUsages.map(({ label, usage }) => [
                label,
                figure(usage),
              ])}
            />
          </Section>
        ) : null}
      </div>

      <Section title={t("zenith.zone_accounts.water_classes")}>
        {zone.waterClasses?.length ? (
          <ul className="space-y-2.5">
            {zone.waterClasses.map((waterClass: any) => (
              <WaterClassCard
                key={waterClass.id}
                waterClass={waterClass}
                asAt={asAt}
              />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-400">
            {t("extraction_right.no_data")}
          </p>
        )}
      </Section>
    </div>
  );
};

/** A region named by its small uppercase heading. */
const Section: React.FunctionComponent<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  const titleId = useId();

  return (
    <section aria-labelledby={titleId}>
      <h2
        id={titleId}
        className="mb-2.5 text-xs font-bold uppercase tracking-widest text-gray-500"
      >
        {title}
      </h2>
      {children}
    </section>
  );
};

/** Label and value pairs, one per ruled line. */
export const Rows: React.FunctionComponent<{
  rows: [string, React.ReactNode][];
}> = ({ rows }) => (
  <dl className="text-sm">
    {rows.map(([label, value]) => (
      <div
        key={label}
        className="flex justify-between gap-4 border-b border-gray-200 py-2.5"
      >
        <dt className="text-gray-500">{label}</dt>
        <dd className="text-right font-medium text-gray-900">{value}</dd>
      </div>
    ))}
  </dl>
);

/** A water class's allocation, item by item, and what remains of it as at a date. */
const WaterClassCard: React.FunctionComponent<{
  waterClass: any;
  asAt?: string;
}> = ({ waterClass, asAt }) => {
  const { t } = useTranslation();
  const nameId = useId();
  const noPeriod = waterClass.balance === null;
  const { remaining, used } = volumesOf(waterClass);
  const { announced, traded, forwardDraw, repaid, available } =
    allocationOf(waterClass);
  const specialAnnouncement = +(waterClass.specialAnnouncementUsage ?? 0);
  const specialOveruse = +(waterClass.specialAnnouncementOverusage ?? 0);
  const entitlements = sortBy(waterClass.extractionRights ?? [], "name")
    .filter(
      (right: any) => right.extractionRightTypeId !== extractionRightTypes.PT,
    )
    .map((right: any) => right.name);
  // An item that is nil this period is left out.
  const figures: [string, number, boolean][] = [
    [t("subscriber.announced_allocations"), announced, true],
    [t("subscriber.approved_transfers"), traded, traded !== 0],
    [
      t("subscriber.special_announcement"),
      -specialAnnouncement,
      specialAnnouncement !== 0,
    ],
    [t("subscriber.forward_draw_volume"), forwardDraw, forwardDraw > 0],
    [
      t("subscriber.forward_draw_volume_of_last_accounting_period"),
      -repaid,
      repaid > 0,
    ],
    [t("subscriber.volume_available"), available, true],
    [t("subscriber.volume_used"), used, true],
    [t("subscriber.special_overuse"), specialOveruse, specialOveruse !== 0],
  ];

  return (
    <li
      aria-labelledby={nameId}
      className="rounded-lg border border-gray-200 px-4 py-3.5"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 id={nameId} className="text-sm font-semibold text-gray-900">
          {waterClass.name}
        </h3>
        {waterClass.hasActiveRate ? (
          <Tag status="success">
            {t("common.allocation")}: {Math.round(waterClass.rate * 100)}%
          </Tag>
        ) : null}
      </div>
      <Rows
        rows={[
          ...(noPeriod
            ? []
            : figures
                .filter(([, , show]) => show)
                .map(
                  ([label, litres]) =>
                    [label, formatVolume(litres)] as [string, React.ReactNode],
                )),
          [
            t("subscriber.extraction_rights"),
            entitlements.join(", ") ||
              t("extraction_right.no_extraction_rights"),
          ],
          [t("extraction_right.total_volume"), formatVolume(waterClass.volume)],
        ]}
      />
      {noPeriod ? (
        <p className="mt-3 text-sm font-semibold text-gray-500">
          {t("zenith.dashboard.no_active_period")}
        </p>
      ) : (
        <div className="mt-3">
          <div
            className={
              remaining < 0
                ? "text-2xl font-bold text-red-500"
                : "text-2xl font-bold text-green-600"
            }
          >
            {formatVolume(remaining)}
          </div>
          <div className="mt-0.5 text-xs text-gray-500">
            {asAt
              ? t("zenith.zone_accounts.remaining_as_at", {
                  date: formatSchemeDay(asAt),
                })
              : t("zenith.zone_accounts.available_balance")}
          </div>
        </div>
      )}
    </li>
  );
};

export default ZoneAccountSummary;
